import { FilterLines, LayersThree02 } from '@untitled-ui/icons-react/build/cjs';
import moment from 'moment';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import ButtonCmp from 'src/components/ButtonCmp';
import NotFoundUI from 'src/components/NotFoundUI';
import SelectBox from 'src/components/SelectBox/SelectBox';
import TabButton from 'src/components/TabButton';
import { appearance } from 'src/redux/basic.slice';
import type { RootState } from 'src/redux/store';
import { getOrdersSummary } from 'src/services/OrderService';
import { getSelectBoxOptions } from 'src/utils/CommonFunctions';

import SlickSlider from './SlickSlider';

const LIMIT = 20;

const UpcomingPickupDelivery = () => {
    const { t } = useTranslation();

    const appearanceData = useSelector(appearance);
    const timezone = appearanceData?.timezone || 'UTC';

    // Shipper/consignee lists come from the single app-boot /common/init call — this screen must
    // not fetch them again. Only the filtered select options are local, since the dropdowns filter
    // the full lists client-side as the user types.
    const { shippers: allShippers, consignees: allConsignees, isShipperLoading } = useSelector((state: RootState) => state.addresses);
    const [shipperOptions, setShipperOptions] = useState<any[]>([]);
    const [consigneeOptions, setConsigneeOptions] = useState<any[]>([]);

    const [activeTab, setActiveTab] = useState('all');
    const [selectedShipper, setSelectedShipper] = useState<any>(null);
    const [selectedConsignee, setSelectedConsignee] = useState<any>(null);
    const [formData, setFormData] = useState({
        startDate: moment().startOf('month').toDate(),
        endDate: moment().endOf('month').toDate(),
    });
    // Filter panel state
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const filterPanelRef = useRef<HTMLDivElement>(null);

    // appliedTimeRange = the committed value (drives the red dot); selectTimeRange = staged value
    // shown in the panel's select, only committed on Apply Filters.
    const [appliedTimeRange, setAppliedTimeRange] = useState<any>('this_month');
    const [selectTimeRange, setSelectTimeRange] = useState<any>('this_month');
    const [stagedShipper, setStagedShipper] = useState<any>(null);
    const [stagedConsignee, setStagedConsignee] = useState<any>(null);

    const timeRangeOptions = [
        { value: 'today', label: 'Today' },
        { value: 'this_week', label: 'This Week' },
        { value: 'this_month', label: 'This Month' },
        { value: 'last_month', label: 'Last Month' },
        { value: 'this_year', label: 'This Year' },
    ];

    const getDateRangeFromOption = (value: string) => {
        switch (value) {
            case 'today':
                return { startDate: moment().startOf('day').toDate(), endDate: moment().endOf('day').toDate() };
            case 'this_week':
                return { startDate: moment().startOf('week').toDate(), endDate: moment().endOf('week').toDate() };
            case 'this_month':
                return { startDate: moment().startOf('month').toDate(), endDate: moment().endOf('month').toDate() };
            case 'last_month':
                return { startDate: moment().subtract(1, 'month').startOf('month').toDate(), endDate: moment().subtract(1, 'month').endOf('month').toDate() };
            case 'this_year':
                return { startDate: moment().startOf('year').toDate(), endDate: moment().endOf('year').toDate() };
            default:
                return { startDate: moment().startOf('month').toDate(), endDate: moment().endOf('month').toDate() };
        }
    };

    // Close filter panel when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (filterPanelRef.current && !event.composedPath().includes(filterPanelRef.current)) {
                setIsFilterOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);

        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Per-tab record lists
    // allRecords = every record loaded so far (pickup + delivery merged)
    // pickupRecords / deliveryRecords = derived from allRecords initially,
    //   then extended independently when user scrolls within that tab
    const [allRecords, setAllRecords] = useState<any[]>([]);
    const [pickupRecords, setPickupRecords] = useState<any[]>([]);
    const [deliveryRecords, setDeliveryRecords] = useState<any[]>([]);

    // "all" tab pagination
    const [allPage, setAllPage] = useState(1);
    const [allHasMore, setAllHasMore] = useState(false);

    // Independent pagination for pickup / delivery tabs
    // page = 0 means "not yet independently loaded — data comes from the all-tab call"
    const [pickupPage, setPickupPage] = useState(0);
    const [deliveryPage, setDeliveryPage] = useState(0);
    const [pickupHasMore, setPickupHasMore] = useState(false);
    const [deliveryHasMore, setDeliveryHasMore] = useState(false);

    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    // Ref so load-more callbacks always read the latest pagination state
    const stateRef = useRef({
        allPage,
        allHasMore,
        pickupPage,
        pickupHasMore,
        deliveryPage,
        deliveryHasMore,
        isLoadingMore,
    });
    stateRef.current = { allPage, allHasMore, pickupPage, pickupHasMore, deliveryPage, deliveryHasMore, isLoadingMore };

    // Seed the unfiltered dropdown options from the boot data. Note this builds options from the
    // FULL lists — the shipperOptions/consigneeOptions already in Redux are the 5-item autocomplete
    // slices used elsewhere, which would silently truncate this filter panel.
    useEffect(() => {
        setShipperOptions(getSelectBoxOptions(allShippers ?? [], 'id', 'companyName'));
    }, [allShippers]);

    useEffect(() => {
        setConsigneeOptions(getSelectBoxOptions(allConsignees ?? [], 'id', 'companyName'));
    }, [allConsignees]);

    // Build filter params shared by all API calls
    const buildBaseParams = useCallback(
        () => ({
            limit: LIMIT,
            startDate: moment(formData.startDate).format('YYYY-MM-DD'),
            endDate: moment(formData.endDate).format('YYYY-MM-DD'),
            shipperId: selectedShipper?.value ?? null,
            consigneeId: selectedConsignee?.value ?? null,
        }),
        [formData, selectedShipper, selectedConsignee]
    );

    // Initial load: call tab=all once, derive pickup/delivery by type
    useEffect(() => {
        let cancelled = false;

        const doFetch = async () => {
            setIsLoading(true);

            try {
                const res: any = await getOrdersSummary({
                    tab: 'all',
                    page: 1,
                    limit: LIMIT,
                    startDate: moment(formData.startDate).format('YYYY-MM-DD'),
                    endDate: moment(formData.endDate).format('YYYY-MM-DD'),
                    shipperId: selectedShipper?.value ?? null,
                    consigneeId: selectedConsignee?.value ?? null,
                });

                if (cancelled) return;

                const data: any[] = res?.data ?? [];
                const hasNext: boolean = res?.pagination?.hasNextPage ?? false;

                setAllRecords(data);
                setPickupRecords(data.filter((r: any) => r.type === 'pickup'));
                setDeliveryRecords(data.filter((r: any) => r.type === 'delivery'));
                setAllPage(1);
                setAllHasMore(hasNext);

                // Reset independent tab pagination; hasMore is unknown until first independent fetch
                setPickupPage(0);
                setDeliveryPage(0);
                setPickupHasMore(false);
                setDeliveryHasMore(false);
            } catch (error) {
                if (cancelled) return;
                console.error('Error fetching orders summary:', error);
                setAllRecords([]);
                setPickupRecords([]);
                setDeliveryRecords([]);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        doFetch();

        return () => {
            cancelled = true;
        };
    }, [formData, selectedShipper, selectedConsignee]);

    // Load-more for "all" tab: call tab=all next page, split-append to pickup/delivery
    const loadMoreAll = useCallback(async () => {
        const { allHasMore: hasMore, allPage: page, isLoadingMore: loading } = stateRef.current;
        if (loading || !hasMore) return;

        setIsLoadingMore(true);

        try {
            const nextPage = page + 1;
            const res: any = await getOrdersSummary({ ...buildBaseParams(), tab: 'all', page: nextPage });
            const newData: any[] = res?.data ?? [];

            setAllRecords((prev) => [...prev, ...newData]);
            setPickupRecords((prev) => [...prev, ...newData.filter((r: any) => r.type === 'pickup')]);
            setDeliveryRecords((prev) => [...prev, ...newData.filter((r: any) => r.type === 'delivery')]);
            setAllPage(nextPage);
            setAllHasMore(res?.pagination?.hasNextPage ?? false);
        } catch (error) {
            console.error('Error loading more (all tab):', error);
        } finally {
            setIsLoadingMore(false);
        }
    }, [buildBaseParams]);

    // Load-more for pickup tab: call tab=pickup independently, deduplicate before appending
    const loadMorePickup = useCallback(async () => {
        const { pickupHasMore: hasMore, pickupPage: page, isLoadingMore: loading } = stateRef.current;
        if (loading) return;
        // page=0 means first independent call (page 1); after that check hasMore
        if (page > 0 && !hasMore) return;

        setIsLoadingMore(true);

        try {
            const nextPage = page === 0 ? 1 : page + 1;
            const res: any = await getOrdersSummary({ ...buildBaseParams(), tab: 'pickup', page: nextPage });
            const newRecords: any[] = res?.data ?? [];

            // Deduplicate so we don't show cards already derived from the "all" call
            setPickupRecords((prev) => {
                const existingKeys = new Set(prev.map((r: any) => `${r.orderId}_${r.orderLegId}_${r.type}`));

                return [...prev, ...newRecords.filter((r: any) => !existingKeys.has(`${r.orderId}_${r.orderLegId}_${r.type}`))];
            });
            setAllRecords((prev) => {
                const existingKeys = new Set(prev.map((r: any) => `${r.orderId}_${r.orderLegId}_${r.type}`));

                return [...prev, ...newRecords.filter((r: any) => !existingKeys.has(`${r.orderId}_${r.orderLegId}_${r.type}`))];
            });
            setPickupPage(nextPage);
            setPickupHasMore(res?.pagination?.hasNextPage ?? false);
        } catch (error) {
            console.error('Error loading more pickups:', error);
        } finally {
            setIsLoadingMore(false);
        }
    }, [buildBaseParams]);

    // Load-more for delivery tab: same pattern as pickup
    const loadMoreDelivery = useCallback(async () => {
        const { deliveryHasMore: hasMore, deliveryPage: page, isLoadingMore: loading } = stateRef.current;
        if (loading) return;
        if (page > 0 && !hasMore) return;

        setIsLoadingMore(true);

        try {
            const nextPage = page === 0 ? 1 : page + 1;
            const res: any = await getOrdersSummary({ ...buildBaseParams(), tab: 'delivery', page: nextPage });
            const newRecords: any[] = res?.data ?? [];

            setDeliveryRecords((prev) => {
                const existingKeys = new Set(prev.map((r: any) => `${r.orderId}_${r.orderLegId}_${r.type}`));

                return [...prev, ...newRecords.filter((r: any) => !existingKeys.has(`${r.orderId}_${r.orderLegId}_${r.type}`))];
            });
            setAllRecords((prev) => {
                const existingKeys = new Set(prev.map((r: any) => `${r.orderId}_${r.orderLegId}_${r.type}`));

                return [...prev, ...newRecords.filter((r: any) => !existingKeys.has(`${r.orderId}_${r.orderLegId}_${r.type}`))];
            });
            setDeliveryPage(nextPage);
            setDeliveryHasMore(res?.pagination?.hasNextPage ?? false);
        } catch (error) {
            console.error('Error loading more deliveries:', error);
        } finally {
            setIsLoadingMore(false);
        }
    }, [buildBaseParams]);

    const handleLoadMore = useCallback(() => {
        if (activeTab === 'all') {
            loadMoreAll();
        } else if (activeTab === 'UpcomingPickups') {
            loadMorePickup();
        } else if (activeTab === 'UpcomingDeliveries') {
            loadMoreDelivery();
        }
    }, [activeTab, loadMoreAll, loadMorePickup, loadMoreDelivery]);

    // When switching to pickup/delivery tab with page=0 (not yet independently fetched),
    // proactively trigger load-more so the loader slide resolves instead of showing forever.
    useEffect(() => {
        if (activeTab === 'UpcomingPickups' && stateRef.current.pickupPage === 0 && stateRef.current.pickupHasMore) {
            loadMorePickup();
        } else if (activeTab === 'UpcomingDeliveries' && stateRef.current.deliveryPage === 0 && stateRef.current.deliveryHasMore) {
            loadMoreDelivery();
        }
    }, [activeTab]);

    const getActiveRecords = () => {
        if (activeTab === 'UpcomingPickups') return pickupRecords;
        if (activeTab === 'UpcomingDeliveries') return deliveryRecords;

        return allRecords;
    };

    const getHasMore = () => {
        if (activeTab === 'UpcomingPickups') return pickupHasMore;
        if (activeTab === 'UpcomingDeliveries') return deliveryHasMore;

        return allHasMore;
    };

    const upcomingPickupDeliveryArr = [
        { value: 'all', name: t('Common.All_Shipments'), isAllow: true, count: allRecords.length },
        { value: 'UpcomingPickups', name: t('Dashboard.UpcomingPickupDelivery.Upcoming_Pickups'), isAllow: true, count: pickupRecords.length },
        { value: 'UpcomingDeliveries', name: t('Dashboard.UpcomingPickupDelivery.Upcoming_Deliveries'), isAllow: true, count: deliveryRecords.length },
    ];

    const activeRecords = getActiveRecords();
    const hasRecords = activeRecords.length > 0;
    const hasMore = getHasMore();

    // Red dot on the filter button — any filter with a value counts, including the default
    // 'this_month' time range (it's pre-selected on landing, so the dot shows from the start).
    // Only clearing the time select (and applying) removes it from the count.
    const appliedFilterCount = [selectedShipper, selectedConsignee, appliedTimeRange].filter(Boolean).length;

    const handleApplyFilters = () => {
        // Data fetch still falls back to this month's range when the time select is cleared,
        // but appliedTimeRange stays null so the dot reflects what the select visibly shows.
        const dateRange = getDateRangeFromOption(selectTimeRange ?? 'this_month');
        setAppliedTimeRange(selectTimeRange ?? null);
        setFormData(dateRange);
        setSelectedShipper(stagedShipper);
        setSelectedConsignee(stagedConsignee);
        setIsFilterOpen(false);
    };

    // Reset only clears the STAGED values — the user still has to hit Apply Filters,
    // same behavior as the ActivityFeed filter panel below this section.
    const handleResetFilters = () => {
        setSelectTimeRange('this_month');
        setStagedShipper(null);
        setStagedConsignee(null);
    };

    const handleOpenFilter = () => {
        // Sync staged state with applied state when opening
        setSelectTimeRange(appliedTimeRange);
        setStagedShipper(selectedShipper);
        setStagedConsignee(selectedConsignee);
        setIsFilterOpen((prev) => !prev);
    };

    return (
        <div className="flex flex-col space-y-5 mb-3">
            <div className="flex items-center justify-between">
                <TabButton
                    tabArray={upcomingPickupDeliveryArr}
                    tabParentClassName="!w-full"
                    isActive={activeTab}
                    parentClassName="tab-border-bottom-parent w-full !shadow-none !border-b !border-borderPrimary"
                    activeClassName="tab-border-bottom-active"
                    className="tab-border-bottom min-w-[240px] max-w-[240px] flex-1"
                    tabBadgeClassName="tab-border-bottom-badge"
                    handleOnClick={(e: any) => {
                        setActiveTab(e?.target?.dataset?.value);
                    }}
                />
                <div className="relative ml-2 flex-shrink-0" ref={filterPanelRef}>
                    <ButtonCmp size="square-md" className={`btn_secondary_black relative `} onClick={handleOpenFilter}>
                        <FilterLines className="w-4 h-4" />
                        {appliedFilterCount > 0 && <span className="absolute top-[3px] right-[3px] w-1.5 h-1.5 rounded-full bg-fgErrorPrimary flex items-center justify-center leading-none"></span>}
                    </ButtonCmp>

                    {isFilterOpen && (
                        <div className="absolute right-0 top-full mt-1 z-[99] w-[260px] bg-white rounded-lg shadow-md border border-borderPrimary flex flex-col">
                            <div className="p-3 flex flex-col gap-3">
                                <div className="flex flex-col">
                                    <SelectBox
                                        name="timeRangeFilter"
                                        id="timeRangeFilter"
                                        labelClassName="form_label mb-1.5 block"
                                        label="Select Time"
                                        placeholder="Select Time"
                                        options={timeRangeOptions}
                                        value={timeRangeOptions.find((o) => o.value === selectTimeRange)}
                                        onChangeFunc={(option: any) => setSelectTimeRange(option?.value ?? null)}
                                        isClearable
                                        isSearchable={false}
                                    />
                                </div>
                                <div className="flex flex-col">
                                    <SelectBox
                                        name="shipperFilter"
                                        labelClassName="form_label mb-1.5 block"
                                        label="Select Shipper"
                                        id="shipperFilter"
                                        placeholder="Select shipper"
                                        isClearable
                                        isSearchable
                                        isLoading={isShipperLoading}
                                        options={shipperOptions}
                                        onChangeFunc={(value: any) => setStagedShipper(value ?? null)}
                                        onInputChange={(inputValue: string) => {
                                            const filtered = inputValue ? (allShippers ?? []).filter((s: any) => s.companyName?.toLowerCase().includes(inputValue.toLowerCase())) : allShippers ?? [];
                                            setShipperOptions(getSelectBoxOptions(filtered, 'id', 'companyName'));
                                        }}
                                        value={stagedShipper}
                                    />
                                </div>
                                <div className="flex flex-col">
                                    <SelectBox
                                        label="Select Consignee"
                                        labelClassName="form_label mb-1.5 block"
                                        name="consigneeFilter"
                                        id="consigneeFilter"
                                        className="form_control"
                                        placeholder="Select consignee"
                                        isClearable
                                        isSearchable
                                        isLoading={isShipperLoading}
                                        options={consigneeOptions}
                                        onChangeFunc={(value: any) => setStagedConsignee(value ?? null)}
                                        onInputChange={(inputValue: string) => {
                                            const filtered = inputValue
                                                ? (allConsignees ?? []).filter((c: any) => c.companyName?.toLowerCase().includes(inputValue.toLowerCase()))
                                                : allConsignees ?? [];
                                            setConsigneeOptions(getSelectBoxOptions(filtered, 'id', 'companyName'));
                                        }}
                                        value={stagedConsignee}
                                    />
                                </div>
                            </div>
                            <div className="flex gap-3 pt-3 pb-3 border-t bg-gray50 rounded-b-lg border-borderPrimary px-3">
                                <ButtonCmp className="btn_secondary_black flex-1" onClick={handleResetFilters}>
                                    Reset All
                                </ButtonCmp>
                                <ButtonCmp className="btn_primary flex-1" onClick={handleApplyFilters}>
                                    Apply Filters
                                </ButtonCmp>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div className="w-full bg-white rounded-xl shadow border border-utilityGray200 flex justify-between flex-col flex-1">
                <div className="flex flex-col w-full">
                    {isLoading || hasRecords ? (
                        <div className="custom-scrollbar scrollbar-hide">
                            <div className="px-3">
                                <SlickSlider records={activeRecords} timezone={timezone} hasMore={hasMore} onLoadMore={handleLoadMore} isLoadingMore={isLoadingMore} isLoading={isLoading} />
                            </div>
                        </div>
                    ) : (
                        <div className="">
                            <NotFoundUI
                                title={t('Dashboard.UpcomingPickupDelivery.Clean_Slate_Title')}
                                desc={t('Dashboard.UpcomingPickupDelivery.No_Orders_Pickup')}
                                containerClassName="!h-auto !my-[0px] min-h-[292px]"
                                isEmptyBackground={true}
                                iconClassName="!hidden"
                                iconSecondaryClassName="!flex"
                                iconSecondary={<LayersThree02 className="w-5 h-5 text-primary" />}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UpcomingPickupDelivery;
